/** Shell scaffold — static HTML template for the A+B->C workspace, the aspect-ratio dropdown fill, and HTML-escaping helpers. Split from dialog-shell.js (1 file 1 duty). */

import { store } from '../../../core/store.js'

// A+B->C workspace. why → R shell + image-is-the-prompt (bao 2026-06-23).
/**
 * Build the static workspace markup (tools column + A/B/C canvas + lightbox).
 * @returns {string} HTML string injected into #mode-create by initDialogShell
 */
export function shellScaffoldHTML() {
  return `
    <div class="cw-shell" id="cwShell">
      <h1 class="sr-only">Create</h1>

      <!-- LEFT: actions (intent picker) + optional note. Slim — no scroll. -->
      <aside class="cw-tools">
        <div class="cw-plays" id="cwPlays" aria-label="Actions"></div>
        <div class="cw-glabel">Add a note (optional)</div>
        <div class="cw-promptbox">
          <i class="ph ph-textbox" aria-hidden="true"></i>
          <textarea id="promptInput" rows="2" placeholder="Anything to add? Leave blank for a smart default"></textarea>
        </div>
      </aside>

      <!-- CANVAS: the explicit A + B -> C flow (R workspace). -->
      <main class="cw-canvas">
        <div class="cw-abc">
          <div class="cw-ab">
            <div class="cw-slot" id="dialogSource" role="button" tabindex="0" aria-label="A — source image, click to pick or drop" title="Pick the A source image">
              <span class="cw-slot-tag">A · Source</span>
              <i class="ph ph-image cw-slot-ic" id="dialogSourceIcon" aria-hidden="true"></i>
              <span class="cw-slot-lbl" id="dialogSourceLabel">Drop or click</span>
              <img id="dialogSourceImg" class="cw-slot-img hidden" alt="A source" />
            </div>
            <span class="cw-op" id="cwPlusB" aria-hidden="true">+</span>
            <div class="cw-slot cw-slot--ref" id="dialogReference" role="button" tabindex="0" aria-label="B — reference image, optional, click to pick" title="B reference image">
              <span class="cw-slot-tag">B · Reference</span>
              <i class="ph ph-plus-square cw-slot-ic" id="dialogReferenceIcon" aria-hidden="true"></i>
              <span class="cw-slot-lbl" id="dialogReferenceLabel">Add reference</span>
              <img id="dialogReferenceImg" class="cw-slot-img hidden" alt="B reference" />
              <input type="file" id="dialogReferenceInput" accept="image/jpeg,image/png,image/webp,image/gif" class="hidden" />
            </div>
          </div>
          <span class="cw-op cw-arrow" aria-hidden="true"><i class="ph ph-arrow-right"></i></span>
          <div class="cw-result" id="dialogResult" role="button" tabindex="0" aria-label="C — result, click to enlarge" title="Result">
            <span class="cw-slot-tag">C · Result</span>
            <div id="dialogResultPh"><i class="ph ph-image-square" aria-hidden="true"></i><span>Pick A, choose an action, then Generate</span></div>
            <img id="dialogResultImg" class="hidden" alt="Result" />
            <div class="style-grid-wrap hidden" id="styleGridWrap"></div>
            <div class="result-actions hidden" id="dialogResultActions">
              <button type="button" class="result-action-btn result-continue" id="dialogResultContinue" title="Keep editing this version"><i class="ph ph-arrow-bend-up-left"></i><span>Refine</span></button>
              <button type="button" class="result-action-btn result-keep" id="dialogResultKeep" title="Keep"><i class="ph ph-bookmark-simple"></i><span>Keep</span></button>
              <button type="button" class="result-action-btn result-share" id="dialogResultShare" title="Share as a social post"><i class="ph ph-share-network"></i><span>Share</span></button>
              <button type="button" class="result-action-btn result-discard" id="dialogResultDiscard" title="Discard"><i class="ph ph-x"></i><span>Discard</span></button>
            </div>
          </div>
        </div>

        <div class="cw-genbar">
          <div class="cw-ctrl">
            <button class="pill-btn" id="aspectBtn" type="button"><i class="ph ph-aspect-ratio" aria-hidden="true"></i><span class="aspect-label">3:4</span></button>
            <div class="aspect-dropdown hidden" id="aspectDropdown"></div>
          </div>
          <button class="cw-gen" id="generateBtn" type="button" title="Generate"><i class="ph ph-magic-wand" aria-hidden="true"></i><span>Generate</span></button>
          <button class="pill-btn" id="stylesBtn" type="button" title="Explore 9 styles on a 3x3 sheet"><i class="ph ph-sparkle" aria-hidden="true"></i><span>Styles</span></button>
        </div>

        <!-- Funnel: the only revenue step is the ORDER. The user puts their design
             on a product and BUYS it (get/purchase) — NOT "sell": creator earnings
             from others buying are a future, unbuilt mechanism. why → commercial-model
             §1/§5 + bao 2026-06-26 (the business is purchase, not resale). -->
        <div class="cw-funnel hidden" id="funnelCta">
          <button class="cw-get" type="button" id="funnelGet"><i class="ph ph-shopping-bag"></i>Put it on a product &amp; get yours →</button>
        </div>
        <div class="mockup-tiles-row" id="mockupTilesRow"></div>
      </main>

      <input type="file" id="createFileInput" accept="image/jpeg,image/png,image/webp,image/gif" class="hidden" />
      <div class="lightbox hidden" id="createLightbox">
        <img id="createLightboxImg" alt="Preview" />
        <button class="lightbox-close" id="createLightboxClose"><i class="ph ph-x"></i></button>
      </div>
    </div>
  `
}

// Aspect-ratio dropdown — built once (not per render). why → workspace scaffold.
const ASPECTS = ['1:1', '4:3', '3:4', '16:9', '9:16']

/**
 * Fill the aspect-ratio dropdown with the fixed ratio list, marking the
 * store-current ratio active.
 * @param {HTMLElement} container - the mounted workspace root
 */
export function fillAspectDropdown(container) {
  const dd = container.querySelector('#aspectDropdown')
  if (!dd) return
  const cur = store.getState('aspectRatio') || '3:4'
  dd.innerHTML = ASPECTS.map(r => `<div class="aspect-item${r === cur ? ' active' : ''}" data-ratio="${r}">${r}</div>`).join('')
}

/**
 * Escape a value for safe HTML text interpolation.
 * @param {*} s
 * @returns {string}
 */
export function escHtml(s) { return String(s).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;') }

/**
 * Escape a value for safe HTML attribute interpolation.
 * @param {*} s
 * @returns {string}
 */
export function escAttr(s) { return escHtml(s).replaceAll('"', '&quot;') }
